/**
 * LocalStorage stash for the Google / Apple / X connect-flow intent —
 * relocated from the frontend into the SDK (auth group D).
 *
 * Lives in `src/platform/web/auth/` because it uses `window.localStorage`
 * — a web-only API. RN callers have no equivalent (the RN connect flow
 * uses deep-link state instead).
 *
 * Persists across the redirect when Turnkey's popup mode is blocked
 * (Safari / mobile) and the user is bounced through the provider's OAuth
 * screen. localStorage rather than sessionStorage because the return can
 * land in a different browsing context: on mobile the X app finishes the
 * authorisation and hands the redirect to a new browser tab, where
 * sessionStorage is empty. The Turnkey kit keeps its PKCE verifier in
 * localStorage for the same reason. Read on return by `_callback-client`
 * to route the user back to the originating page (settings / onboarding)
 * and surface the OIDC sub-claim to the resume effect.
 *
 * X popup-mode connect rides `useOauthConnect` and may write here; the
 * Safari/mobile X path still uses the backend-mediated flow (`useXConnect`),
 * which carries its own state via a server-signed cookie.
 *
 * Stored under STASH_KEY (versioned). Auto-expires after TTL_MS so a
 * stale stash from an abandoned flow can never trigger a surprise dialog
 * days later.
 */

const STASH_KEY: string = 'morfi.oauth-connect.intent.v1';
const TTL_MS: number = 10 * 60 * 1000;

export type OauthConnectProvider = 'google' | 'apple' | 'x';

export interface OauthConnectStashEntry {
  flow: 'connect';
  provider: OauthConnectProvider;
  returnTo: string;
  startedAt: number;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

// ALLOW-UNKNOWN: shape-guard for raw JSON.parse output from localStorage
function isStashEntry(value: unknown): value is OauthConnectStashEntry {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  if (v.flow !== 'connect') return false;
  if (v.provider !== 'google' && v.provider !== 'apple' && v.provider !== 'x') return false;
  if (typeof v.returnTo !== 'string') return false;
  if (typeof v.startedAt !== 'number') return false;
  return true;
}

function clearRaw(): void {
  if (!isBrowser()) return;
  window.localStorage.removeItem(STASH_KEY);
}

export const OauthConnectStash = {
  write(entry: Omit<OauthConnectStashEntry, 'flow' | 'startedAt'>): void {
    if (!isBrowser()) return;
    const full: OauthConnectStashEntry = {
      flow: 'connect',
      provider: entry.provider,
      returnTo: entry.returnTo,
      startedAt: Date.now(),
    };
    window.localStorage.setItem(STASH_KEY, JSON.stringify(full));
  },

  /** Non-destructive read. Auto-clears + returns null if expired or malformed. */
  read(): OauthConnectStashEntry | null {
    if (!isBrowser()) return null;
    const raw = window.localStorage.getItem(STASH_KEY);
    if (raw === null) return null;
    // ALLOW-UNKNOWN: JSON.parse return is unknown by design; narrowed below via isStashEntry
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      // SILENT-CATCH: corrupt stash from a prior version — drop it
      clearRaw();
      return null;
    }
    if (!isStashEntry(parsed)) {
      clearRaw();
      return null;
    }
    if (Date.now() - parsed.startedAt > TTL_MS) {
      clearRaw();
      return null;
    }
    return parsed;
  },

  /** Read + clear in one call. */
  consume(): OauthConnectStashEntry | null {
    const entry = OauthConnectStash.read();
    if (entry !== null) clearRaw();
    return entry;
  },

  clear(): void {
    clearRaw();
  },
};
